/**
 * Workflow Detail Drawer
 * 
 * A slide-out panel showing full workflow instance details with steps, 
 * activity log, and actions.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  Workflow,
  CheckCircle2,
  Clock,
  Play,
  AlertCircle,
  User,
  Calendar,
  MessageSquare,
  Loader2,
  XCircle,
  ChevronRight,
  Activity,
  FileText,
} from 'lucide-react';
import * as workflowApi from '../../api/workflows';
import CompleteStepDialog from './CompleteStepDialog';

// Utility for class names
const cn = (...classes) => classes.filter(Boolean).join(' ');

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-gray-100 text-gray-700', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700', icon: Play },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  skipped: { label: 'Skipped', color: 'bg-yellow-100 text-yellow-700', icon: AlertCircle },
  blocked: { label: 'Blocked', color: 'bg-red-100 text-red-700', icon: AlertCircle },
  active: { label: 'Active', color: 'bg-blue-100 text-blue-700', icon: Play },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-600', icon: XCircle },
};

function StepCard({ stepInstance, isCurrentStep, onComplete }) {
  const statusConfig = STATUS_CONFIG[stepInstance.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;
  const step = stepInstance.step;

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <div
      className={cn(
        "p-3 rounded-lg border transition-colors",
        isCurrentStep ? "border-blue-300 bg-blue-50" : "border-gray-200 bg-white"
      )}
      data-testid={`step-${stepInstance.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-gray-900 text-sm">{step?.name || 'Step'}</h4>
            <Badge className={cn("text-xs", statusConfig.color)}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {statusConfig.label}
            </Badge>
          </div>
          
          {step?.description && (
            <p className="text-xs text-gray-500 mt-1">{step.description}</p>
          )}

          {/* Field values if completed */}
          {stepInstance.field_values && Object.keys(stepInstance.field_values).length > 0 && (
            <div className="mt-2 p-2 bg-gray-50 rounded text-xs space-y-1">
              {Object.entries(stepInstance.field_values).slice(0, 3).map(([key, value]) => (
                <div key={key} className="flex justify-between">
                  <span className="text-gray-500">{key}:</span>
                  <span className="text-gray-700 font-medium">{String(value)}</span>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500">
            {stepInstance.completed_at && (
              <span className="flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" />
                Completed {formatDate(stepInstance.completed_at)}
              </span>
            )}
            {stepInstance.due_date && stepInstance.status !== 'completed' && (
              <span className={cn(
                "flex items-center gap-1",
                new Date(stepInstance.due_date) < new Date() && "text-red-500"
              )}>
                <Calendar className="w-3 h-3" />
                Due {formatDate(stepInstance.due_date)}
              </span>
            )}
          </div>
        </div>

        {(stepInstance.status === 'pending' || stepInstance.status === 'in_progress') && isCurrentStep && (
          <Button
            size="sm"
            onClick={() => onComplete(stepInstance)}
            data-testid={`complete-step-${stepInstance.id}`}
          >
            Complete
          </Button>
        )}
      </div>
    </div>
  );
}

function ActivityLogItem({ activity }) {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'workflow_started':
        return <Play className="w-4 h-4 text-blue-500" />;
      case 'step_completed':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'workflow_completed':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'workflow_cancelled':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'step_assigned':
        return <User className="w-4 h-4 text-blue-500" />;
      case 'field_updated':
        return <FileText className="w-4 h-4 text-gray-500" />;
      default:
        return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <div className="flex gap-3 py-2">
      <div className="flex-shrink-0 mt-0.5">
        {getActionIcon(activity.action)}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900">{activity.action_label || activity.action}</p>
        {activity.details && (
          <p className="text-xs text-gray-500 mt-0.5">{activity.details}</p>
        )}
        <p className="text-xs text-gray-400 mt-1">
          {formatDate(activity.created_at)}
          {activity.user_name && ` by ${activity.user_name}`}
        </p>
      </div>
    </div>
  );
}

export default function WorkflowDetailDrawer({
  open,
  onOpenChange,
  instanceId,
  onUpdate,
}) {
  const queryClient = useQueryClient();
  const [selectedStep, setSelectedStep] = useState(null);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);

  // Fetch instance details
  const { data: instanceData, isLoading } = useQuery({
    queryKey: ['workflow-instance', instanceId],
    queryFn: () => workflowApi.getInstance(instanceId),
    enabled: open && !!instanceId,
  });

  // Fetch activity log
  const { data: activityData } = useQuery({
    queryKey: ['workflow-activity', instanceId],
    queryFn: () => workflowApi.getActivityLog(instanceId),
    enabled: open && !!instanceId,
  });

  const instance = instanceData?.data;
  const activities = activityData?.data || [];
  const stepInstances = instance?.step_instances || [];

  // Complete step mutation
  const completeMutation = useMutation({
    mutationFn: ({ stepInstanceId, completionData }) =>
      workflowApi.completeStep(stepInstanceId, completionData),
    onSuccess: () => {
      queryClient.invalidateQueries(['workflow-instance', instanceId]);
      queryClient.invalidateQueries(['workflow-activity', instanceId]);
      queryClient.invalidateQueries(['my-tasks']);
      toast.success('Step completed successfully');
      setShowCompleteDialog(false);
      setSelectedStep(null);
      if (onUpdate) onUpdate();
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to complete step');
    },
  });

  // Cancel workflow mutation
  const cancelMutation = useMutation({
    mutationFn: () => workflowApi.cancelWorkflow(instanceId, 'Cancelled by user'),
    onSuccess: () => {
      queryClient.invalidateQueries(['workflow-instance', instanceId]);
      queryClient.invalidateQueries(['my-tasks']);
      toast.success('Workflow cancelled');
      if (onUpdate) onUpdate();
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to cancel workflow');
    },
  });

  const handleCompleteStep = (stepInstance) => {
    setSelectedStep(stepInstance);
    setShowCompleteDialog(true);
  };

  const handleCompleteSubmit = (completionData) => {
    if (!selectedStep) return;
    completeMutation.mutate({
      stepInstanceId: selectedStep.id,
      completionData,
    });
  };

  const statusConfig = STATUS_CONFIG[instance?.status] || STATUS_CONFIG.active;
  const StatusIcon = statusConfig.icon;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-lg" data-testid="workflow-detail-drawer">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : !instance ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-500">Workflow not found</p>
            </div>
          ) : (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <Workflow className="w-5 h-5 text-blue-600" />
                  {instance.name || 'Workflow'}
                </SheetTitle>
                <SheetDescription className="flex items-center gap-2">
                  <Badge className={cn(statusConfig.color)}>
                    <StatusIcon className="w-3 h-3 mr-1" />
                    {statusConfig.label}
                  </Badge>
                  {instance.template?.name && (
                    <span className="text-gray-500">• {instance.template.name}</span>
                  )}
                </SheetDescription>
              </SheetHeader>

              <Tabs defaultValue="steps" className="mt-6">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="steps">Steps</TabsTrigger>
                  <TabsTrigger value="activity">Activity</TabsTrigger>
                </TabsList>

                <TabsContent value="steps" className="mt-4">
                  <ScrollArea className="h-[calc(100vh-280px)]">
                    <div className="space-y-2 pr-4">
                      {stepInstances.length === 0 ? (
                        <p className="text-center text-gray-500 py-8">No steps found</p>
                      ) : (
                        stepInstances.map((stepInstance) => (
                          <StepCard
                            key={stepInstance.id}
                            stepInstance={stepInstance}
                            isCurrentStep={instance.current_step_id === stepInstance.step_id}
                            onComplete={handleCompleteStep}
                          />
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="activity" className="mt-4">
                  <ScrollArea className="h-[calc(100vh-280px)]">
                    <div className="divide-y pr-4">
                      {activities.length === 0 ? (
                        <p className="text-center text-gray-500 py-8">No activity yet</p>
                      ) : (
                        activities.map((activity) => (
                          <ActivityLogItem key={activity.id} activity={activity} />
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>
              </Tabs>

              {/* Actions */}
              {(instance.status === 'active' || instance.status === 'in_progress') && (
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t">
                  <Button
                    variant="outline"
                    className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => cancelMutation.mutate()}
                    disabled={cancelMutation.isPending}
                    data-testid="cancel-workflow-btn"
                  >
                    {cancelMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Cancelling...
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4 mr-2" />
                        Cancel Workflow
                      </>
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Complete Step Dialog */}
      {selectedStep && (
        <CompleteStepDialog
          open={showCompleteDialog}
          onOpenChange={setShowCompleteDialog}
          task={selectedStep}
          onComplete={handleCompleteSubmit}
          isLoading={completeMutation.isPending}
        />
      )}
    </>
  );
}
